import { AgGridReact } from "@jsq/ds/core";
import { HeaderWithTooltip } from "~/pages/Compliance/Common/HeaderWithTooltip";
import { ONBOARDING_STATUS_FILTER_VALUES } from "~/pages/Compliance/Common/OnboardingStatus/resolveOnboardingState";
import { onboardingCellClass } from "~/pages/Compliance/Common/OnboardingStatus/styles_vanilla.css";
import { ProgramTagRenderer } from "~/pages/Compliance/Common/ProgramTagRenderer";
import {
  NONE,
  PROGRAM_OPTIONS,
} from "~/pages/Compliance/ComplianceSettingsDrawer/programOptions";
import { type TypedAGGridCellRenderParams } from "~/pages/Compliance/ComplianceTable/types";
import { InvestorProgramRenderer } from "./InvestorProgramRenderer";
import { OnboardingStatusRenderer } from "./OnboardingStatusRenderer";
import { type ComplianceEntityRow } from "./types";

const INVESTOR_PROGRAM_TOOLTIP =
  "The programs that the client, or Juniper Square, applies to the investors in this entity. " +
  "This can include programs that the entity pays Juniper Square to run (e.g. AML/KYC), as well " +
  "as programs that Juniper Square opts to run in order to support other system capabilities " +
  "(e.g. automated payments).";
const ENTITY_PROGRAM_TOOLTIP =
  "The AML/KYC compliance program that Juniper Square applies to the entity itself.";

// The slice matches a text filter by lower-casing both sides and testing for a substring, so
// `contains` is the only option that describes what the server actually does.
const TEXT_FILTER = {
  filter: "agTextColumnFilter",
  filterParams: {
    filterOptions: ["contains"],
    maxNumConditions: 1,
  },
} as const;

// Columns whose values come from a closed set get a checkbox list instead, matched exactly
// server-side (COMP-5056). Each list is spelled out here because the slice returns display
// strings, not enums, so there is nothing to derive them from on the wire.
const setFilter = (values: readonly string[]) =>
  ({ filter: "agSetColumnFilter", filterParams: { values: [...values] } }) as const;

// Main's `entity_status` enum (AccountModel.status): Active | Completed.
const ENTITY_STATUS_FILTER_VALUES = ["Active", "Completed"] as const;

// Main's `product_line` lookup table, seeded with these four rows and never appended to
// outside the seed migration. Entities with no product line come back null and render "--";
// that placeholder is not offered as a checkbox, matching the other compliance grids.
const PRODUCT_LINE_FILTER_VALUES = [
  "Software",
  "Fund Administration",
  "Deal Services",
  "Fund Services",
] as const;

// Entities with no program render "--", so that is what the checkbox offers for them, matching
// the pre-onboarding grid's Compliance program filter (AssignProgramTable's `programsAndEmpty`).
// The datasource sends it as null. "None" is not offered: a program of type None means the same
// thing as unassigned to a reader, and the slice now reports both as no program, so listing it
// as well would give two checkboxes for one state.
const UNASSIGNED_PROGRAM = "--";
const ENTITY_PROGRAM_FILTER_VALUES = [
  UNASSIGNED_PROGRAM,
  ...PROGRAM_OPTIONS.filter((option) => option.label !== NONE).map((option) => option.label),
];

// Column `field`/`colId` values must match the datasource's field maps so server-side
// sort/filter resolve. Entity name is the only free-text column; the rest of the filterable
// columns are closed value sets and take checkbox lists.
//
// `investorProgramValues` are the assignable program names from `programsAssignableToEntities`
// -- the same source the pre-onboarding grid's Compliance program filter uses, so the two views
// offer the same options. Pass none and the column is left unfilterable (the list has not
// loaded yet).
export const getColumnDefs = (
  investorProgramValues: readonly string[] = [],
): AgGridReact.AgGridReactProps["columnDefs"] => [
  {
    field: "name",
    headerName: "Entity",
    sortable: true,
    flex: 2,
    minWidth: 200,
    ...TEXT_FILTER,
  },
  {
    field: "status",
    headerName: "Status",
    sortable: true,
    minWidth: 120,
    ...setFilter(ENTITY_STATUS_FILTER_VALUES),
  },
  {
    field: "productLine",
    headerName: "Product line",
    sortable: true,
    minWidth: 160,
    valueFormatter: (params) => params.value ?? "--",
    ...setFilter(PRODUCT_LINE_FILTER_VALUES),
  },
  {
    colId: "investorProgram",
    headerName: "Investor program",
    sortable: false,
    flex: 1,
    minWidth: 220,
    headerComponent: HeaderWithTooltip,
    // Opens downward: this copy is tall enough that the default `top` crops it above the
    // viewport edge, and the header tooltip does not auto-flip (COMP-4959)
    headerComponentParams: {
      tooltipContent: INVESTOR_PROGRAM_TOOLTIP,
      tooltipPlacement: "bottom",
    },
    // Filters on the compliance program alone, which is what the pre-onboarding grid filtered
    // (COMP-5056). The cell still shows the whole composition -- FATCA/CRS and Legacy KYC are
    // not filter dimensions: FATCA/CRS is additive rather than exclusive, so one checkbox list
    // cannot say whether "KYC" + "FATCA/CRS" means either or both.
    ...(investorProgramValues.length > 0
      ? setFilter([UNASSIGNED_PROGRAM, ...investorProgramValues])
      : {}),
    cellRenderer: (params: TypedAGGridCellRenderParams<ComplianceEntityRow>) =>
      params.data ? (
        <InvestorProgramRenderer
          complianceProgram={params.data.complianceProgram}
          complianceEnabled={params.data.complianceEnabled}
          fatcaCrsEnabled={params.data.fatcaCrsEnabled}
          isInvestorDiligenceEnabled={params.data.isInvestorDiligenceEnabled}
        />
      ) : null,
  },
  {
    field: "entityProgram",
    headerName: "Entity program",
    sortable: true,
    minWidth: 160,
    headerComponent: HeaderWithTooltip,
    headerComponentParams: { tooltipContent: ENTITY_PROGRAM_TOOLTIP },
    ...setFilter(ENTITY_PROGRAM_FILTER_VALUES),
    cellRenderer: (params: TypedAGGridCellRenderParams<ComplianceEntityRow>) =>
      params.data ? (
        <ProgramTagRenderer program={params.data.entityProgram} testId="entity-program-tag" />
      ) : null,
  },
  {
    field: "onboardingStatus",
    headerName: "Onboarding status",
    sortable: true,
    flex: 1,
    minWidth: 200,
    // The row ⋯ menu + Action-required background live inside this cell's renderer; FillCell-style
    // cellClass strips the cell's padding so that background fills edge-to-edge (as on Positions).
    cellRenderer: OnboardingStatusRenderer,
    cellClass: onboardingCellClass,
    // Server-side set filter over the fixed onboarding buckets the slice returns.
    ...setFilter(ONBOARDING_STATUS_FILTER_VALUES),
  },
];
